import React from 'react';
import { useRouteMatch } from 'react-router';
import styled, { css } from 'styled-components';

import MenuLinkItemRRD from 'common/components/MenuLinkItemRRD';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';

export type Props = {
  className?: string;
  count: number;
  disableHoverStyle?: boolean;
  dropActive?: boolean;
  hidden?: boolean;
  link: string;
  name: string | JSX.Element;
  image?: {
    src: string;
    blurhash: string | null;
  };
  style?: object;
  transient?: boolean;
};

const Link = styled(MenuLinkItemRRD)<{
  disableHoverStyle?: boolean;
  hidden?: boolean;
  transient?: boolean;
}>`
  opacity: ${({ transient, hidden }) => (hidden ? 0 : 1) * (transient ? 0.4 : 1)};

  ${({ disableHoverStyle, active }) =>
    // disableHoverStyle prop is a hack to support working around a bug in Chrome which, while dragging
    // an element, applies hover styles to the element that is in the original location of the dragged element.
    // See: https://bugs.webkit.org/show_bug.cgi?id=134555
    disableHoverStyle &&
    !active &&
    css`
      &:not(:disabled) {
        &:hover {
          background: transparent;
        }
      }
    `}
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbnailContainer = styled.div`
  margin-inline-end: 8px;
`;

const NavItem = (
  { className, count, disableHoverStyle, dropActive, hidden, link, name, image, style, transient }: Props,
  ref: React.Ref<HTMLAnchorElement>,
) => {
  const match = useRouteMatch({
    path: link,
    exact: true,
  });

  return (
    <Link
      active={!!match || dropActive}
      className={className}
      disableHoverStyle={disableHoverStyle}
      hidden={hidden}
      href={link}
      ref={ref}
      rightContent={<>{count}</>}
      style={style}
      transient={transient}
    >
      <NameContainer>
        {image && (
          <ThumbnailContainer>
            <LazyLoadedThumbnail
              size="tiny"
              alt={typeof name === 'string' ? name : ''}
              blurhash={image.blurhash}
              src={image.src}
            />
          </ThumbnailContainer>
        )}
        <span>{name}</span>
      </NameContainer>
    </Link>
  );
};

export default React.forwardRef<HTMLAnchorElement, Props>(NavItem);
